import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { ReactNode, useEffect, useMemo, useState } from "react";
import { getGenericModel } from "../../api/db/generic_model";
import { formatDateTimeWithSeconds as formatDateTime } from "../../utils/date-formatting";
import { useGlobalState } from "../globalState";

type NullableString = string | null | undefined;

interface CacheRecordBase {
	id: number;
	created: string;
	updated?: NullableString;
}

interface DropcontactCacheRecord extends CacheRecordBase {
	request_id?: NullableString;
	linkedin_url?: NullableString;
	json?: unknown;
}

interface SnovCacheRecord extends CacheRecordBase {
	linkedin_url?: NullableString;
	json?: unknown;
}

interface RocketreachCacheRecord extends CacheRecordBase {
	linkedin_url?: NullableString;
	json?: unknown;
}

interface ChannelResolveJobRecord extends CacheRecordBase {
	resolver: number;
	status: NullableString;
}

const resolverName = (resolver: number | undefined): string => {
	if (resolver === 0) return "Rocketreach";
	if (resolver === 1) return "Snov";
	return "";
};

export const PersonDataSourcesStatus = ({ personId }: { personId: number }) => {
	const { currentUser } = useGlobalState();

	const isSuperuser = !!currentUser?.is_superuser;

	const [dropcontact, setDropcontact] = useState<DropcontactCacheRecord[]>([]);
	const [snov, setSnov] = useState<SnovCacheRecord[]>([]);
	const [rocketreach, setRocketreach] = useState<RocketreachCacheRecord[]>([]);
	const [jobs, setJobs] = useState<ChannelResolveJobRecord[]>([]);

	useEffect(() => {
		if (!isSuperuser || !personId) return;

		const dcParams = new URLSearchParams();
		dcParams.set("model", "DropcontactCache");
		dcParams.set("person", String(personId));
		dcParams.set("fields", "id,created,request_id,linkedin_url,json");

		const snParams = new URLSearchParams();
		snParams.set("model", "SnovCache");
		snParams.set("person", String(personId));
		snParams.set("fields", "id,created,linkedin_url,json");

		const rrParams = new URLSearchParams();
		rrParams.set("model", "RocketreachCache");
		rrParams.set("person", String(personId));
		rrParams.set("fields", "id,created,updated,linkedin_url,json");

		const crjParams = new URLSearchParams();
		crjParams.set("model", "ChannelResolveJob");
		crjParams.set("person", String(personId));
		crjParams.set("fields", "id,created,updated,resolver,status");

		Promise.all([
			getGenericModel(dcParams),
			getGenericModel(snParams),
			getGenericModel(rrParams),
			getGenericModel(crjParams),
		])
			.then(([dc, sn, rr, crj]) => {
				setDropcontact(dc as DropcontactCacheRecord[]);
				setSnov(sn as SnovCacheRecord[]);
				setRocketreach(rr as RocketreachCacheRecord[]);
				setJobs(crj as ChannelResolveJobRecord[]);
			})
			.catch(() => {
				// Intentionally let errors surface via network console for superusers
			});
	}, [isSuperuser, personId]);

	const latestByCreated = <T extends CacheRecordBase>(list: T[]): T | undefined => {
		if (!list || list.length === 0) return undefined;
		const byDate = [...list].sort((a, b) =>
			a.created < b.created ? 1 : a.created > b.created ? -1 : 0
		);
		return byDate[0];
	};

	const latestDc = useMemo(() => latestByCreated(dropcontact), [dropcontact]);
	const latestSn = useMemo(() => latestByCreated(snov), [snov]);
	const latestRr = useMemo(() => latestByCreated(rocketreach), [rocketreach]);

	if (!isSuperuser) return null;

	return (
		<Grid size={12} data-component="person-data-sources-status">
			<Accordion data-component="person-data-sources-accordion">
				<AccordionSummary
					expandIcon={<ExpandMoreIcon />}
					data-component="person-data-sources-accordion-summary"
				>
					<Typography variant="h6" data-component="person-data-sources-title">
						Data source status
					</Typography>
				</AccordionSummary>
				<AccordionDetails data-component="person-data-sources-accordion-details">
					<Card data-component="person-data-sources-status-card">
						<CardContent data-component="person-data-sources-status-card-content">
							<Stack spacing={2} sx={{ mt: 1 }}>
								<CountsHeader
									dropCount={dropcontact.length}
									snovCount={snov.length}
									rrCount={rocketreach.length}
									jobsCount={jobs.length}
									latestDc={latestDc?.created}
									latestSn={latestSn?.created}
									latestRr={latestRr?.updated || latestRr?.created}
								/>
								<Divider />
								<DropcontactTable rows={dropcontact} />
								<SnovTable rows={snov} />
								<RocketreachTable rows={rocketreach} />
								<JobsTable rows={jobs} />
							</Stack>
						</CardContent>
					</Card>
				</AccordionDetails>
			</Accordion>
		</Grid>
	);
};

export default PersonDataSourcesStatus;

// Small header with counts and latest timestamps
const CountsHeader = ({
	dropCount,
	snovCount,
	rrCount,
	jobsCount,
	latestDc,
	latestSn,
	latestRr,
}: {
	dropCount: number;
	snovCount: number;
	rrCount: number;
	jobsCount: number;
	latestDc?: string;
	latestSn?: string;
	latestRr?: string;
}) => {
	return (
		<Stack spacing={1} data-component="person-data-sources-counts">
			<Stack
				direction="row"
				spacing={1}
				useFlexGap
				flexWrap="wrap"
				alignItems="center"
			>
				<Typography variant="body2" sx={{ minWidth: 180 }}>
					DropcontactCache
				</Typography>
				<Chip label={`total: ${dropCount}`} size="small" />
				{latestDc ? (
					<Chip label={`latest: ${formatDateTime(latestDc)}`} size="small" />
				) : (
					<Chip label="none" size="small" color="default" />
				)}
			</Stack>
			<Stack
				direction="row"
				spacing={1}
				useFlexGap
				flexWrap="wrap"
				alignItems="center"
			>
				<Typography variant="body2" sx={{ minWidth: 180 }}>
					SnovCache
				</Typography>
				<Chip label={`total: ${snovCount}`} size="small" />
				{latestSn ? (
					<Chip label={`latest: ${formatDateTime(latestSn)}`} size="small" />
				) : (
					<Chip label="none" size="small" color="default" />
				)}
			</Stack>
			<Stack
				direction="row"
				spacing={1}
				useFlexGap
				flexWrap="wrap"
				alignItems="center"
			>
				<Typography variant="body2" sx={{ minWidth: 180 }}>
					RocketreachCache
				</Typography>
				<Chip label={`total: ${rrCount}`} size="small" />
				{latestRr ? (
					<Chip label={`latest: ${formatDateTime(latestRr)}`} size="small" />
				) : (
					<Chip label="none" size="small" color="default" />
				)}
			</Stack>
			<Stack
				direction="row"
				spacing={1}
				useFlexGap
				flexWrap="wrap"
				alignItems="center"
			>
				<Typography variant="body2" sx={{ minWidth: 180 }}>
					ChannelResolveJob
				</Typography>
				<Chip label={`total: ${jobsCount}`} size="small" />
			</Stack>
		</Stack>
	);
};

// Generic JSON cell
const JSONCell = ({ value }: { value: unknown }) => {
	if (value === undefined) return null;
	return (
		<pre
			style={{
				maxHeight: 200,
				overflow: "auto",
				margin: 0,
				fontFamily: "monospace",
				whiteSpace: "pre-wrap",
				wordBreak: "break-word",
			}}
		>
			{typeof value === "string"
				? value
				: JSON.stringify(value as Record<string, unknown>, null, 2)}
		</pre>
	);
};

// Generic table
type Column<T> = {
	header: string;
	render: (row: T) => ReactNode;
	width?: string | number;
};

const DataTable = <T extends { id: number }>({
	title,
	rows,
	columns,
	dataComponent,
}: {
	title: string;
	rows: T[];
	columns: Column<T>[];
	dataComponent: string;
}) => {
	const sorted = useMemo(
		() => rows.slice().sort((a, b) => (a.id < b.id ? 1 : -1)),
		[rows]
	);
	return (
		<Card data-component={`${dataComponent}-card`}>
			<CardContent sx={{ overflowX: "auto" }}>
				<Typography variant="subtitle1">{title}</Typography>
				<Table data-component={`${dataComponent}-table`} size="small">
					<TableHead>
						<TableRow>
							{columns.map((c, idx) => (
								<TableCell key={idx} style={{ width: c.width }}>
									{c.header}
								</TableCell>
							))}
						</TableRow>
					</TableHead>
					<TableBody>
						{sorted.map((r) => (
							<TableRow key={r.id} hover>
								{columns.map((c, idx) => (
									<TableCell key={idx}>{c.render(r)}</TableCell>
								))}
							</TableRow>
						))}
					</TableBody>
				</Table>
			</CardContent>
		</Card>
	);
};

// Specific tables
const DropcontactTable = ({ rows }: { rows: DropcontactCacheRecord[] }) => {
	const columns: Column<DropcontactCacheRecord>[] = [
		{ header: "ID", render: (r) => r.id },
		{ header: "Created", render: (r) => formatDateTime(r.created) },
		{ header: "Request ID", render: (r) => r.request_id || "" },
		{ header: "LinkedIn URL", render: (r) => r.linkedin_url || "" },
		{ header: "JSON", render: (r) => <JSONCell value={r.json} /> },
	];
	return (
		<DataTable
			title="DropcontactCache"
			rows={rows}
			columns={columns}
			dataComponent="dropcontact-cache"
		/>
	);
};

const SnovTable = ({ rows }: { rows: SnovCacheRecord[] }) => {
	const columns: Column<SnovCacheRecord>[] = [
		{ header: "ID", render: (r) => r.id },
		{ header: "Created", render: (r) => formatDateTime(r.created) },
		{ header: "LinkedIn URL", render: (r) => r.linkedin_url || "" },
		{ header: "JSON", render: (r) => <JSONCell value={r.json} /> },
	];
	return (
		<DataTable
			title="SnovCache"
			rows={rows}
			columns={columns}
			dataComponent="snov-cache"
		/>
	);
};

const RocketreachTable = ({ rows }: { rows: RocketreachCacheRecord[] }) => {
	const columns: Column<RocketreachCacheRecord>[] = [
		{ header: "ID", render: (r) => r.id },
		{ header: "Created", render: (r) => formatDateTime(r.created) },
		{ header: "Updated", render: (r) => formatDateTime(r.updated || undefined) },
		{ header: "LinkedIn URL", render: (r) => r.linkedin_url || "" },
		{ header: "JSON", render: (r) => <JSONCell value={r.json} /> },
	];
	return (
		<DataTable
			title="RocketreachCache"
			rows={rows}
			columns={columns}
			dataComponent="rocketreach-cache"
		/>
	);
};

const JobsTable = ({ rows }: { rows: ChannelResolveJobRecord[] }) => {
	const columns: Column<ChannelResolveJobRecord>[] = [
		{ header: "ID", render: (r) => r.id },
		{ header: "Created", render: (r) => formatDateTime(r.created) },
		{ header: "Updated", render: (r) => formatDateTime(r.updated || undefined) },
		{ header: "Resolver", render: (r) => resolverName(r.resolver) },
		{ header: "Status", render: (r) => r.status || "" },
	];
	return (
		<DataTable
			title="ChannelResolveJob"
			rows={rows}
			columns={columns}
			dataComponent="channel-resolve-job"
		/>
	);
};
